import { motion } from "framer-motion";
import { Rocket, Gauge, Wifi, Check } from "lucide-react";
import { COMPANY_INFO, TURBO_PACKAGES, fadeInUp, staggerContainer } from "../data/constants";

export const FlowingTurboSection = () => {
  return (
    <section id="turbo" className="py-24 relative overflow-hidden" data-testid="turbo-section">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0a0a1a]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent" />
        {/* Animated grid */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 149, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 149, 255, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        {/* Glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-blue-500/20 text-cyan-400 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Rocket className="w-4 h-4" />
            NUEVA LÍNEA PREMIUM
          </motion.div>
          
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-6xl font-bold text-white mt-4"
          >
            Flowing{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
              Turbo
            </span>
          </motion.h2>
          
          <motion.p variants={fadeInUp} className="text-xl text-gray-400 mt-4 max-w-2xl mx-auto">
            Internet Ultraveloz para los usuarios más exigentes. Velocidades extremas para streaming 4K, gaming y trabajo remoto.
          </motion.p>
        </motion.div>

        {/* Turbo Packages Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {TURBO_PACKAGES.map((pkg, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className={`relative group ${pkg.popular ? 'md:-mt-4 md:mb-4' : ''}`}
            >
              {/* Glow effect on hover */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-3xl blur opacity-30 group-hover:opacity-60 transition-opacity duration-500" />
              
              <div className={`relative bg-[#0f0f2a] rounded-3xl p-8 border border-blue-500/20 hover:border-cyan-500/50 transition-all duration-500 ${pkg.popular ? 'ring-2 ring-cyan-500' : ''}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-1 rounded-full text-sm font-semibold">
                    MÁS POPULAR
                  </div>
                )}
                
                <div className="text-center">
                  {/* Speed indicator */}
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Gauge className="w-10 h-10 text-cyan-400" />
                  </div>
                  
                  <div className="text-6xl font-bold text-white mb-2">{pkg.speed}</div>
                  <div className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-1 rounded-lg text-sm font-bold mb-6">
                    MEGAS
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 text-cyan-400 mb-6">
                    <Wifi className="w-5 h-5" />
                    <span className="text-sm font-medium">Internet Ultraveloz</span>
                  </div>
                  
                  <div className="mb-8">
                    <span className="text-lg text-gray-400">Sólo</span>
                    <span className="text-5xl font-bold text-white ml-2">${pkg.price}</span>
                    <span className="text-gray-400">/mes</span>
                  </div>
                  
                  <ul className="text-left space-y-3 mb-8">
                    <li className="flex items-center gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      Latencia ultra baja
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      Ideal para gaming y streaming 4K
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      Soporte técnico prioritario
                    </li>
                    <li className="flex items-center gap-3 text-gray-300">
                      <Check className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      Instalación express
                    </li>
                  </ul>
                  
                  <a
                    href={COMPANY_INFO.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-4 rounded-full font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                  >
                    ¡Lo Quiero!
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-gray-500 mt-8"
        >
          * Todos los precios incluyen IVA. Consulta disponibilidad en tu zona.
        </motion.p>
      </div>
    </section>
  );
};
